import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import EntitiesList from './EntitiesList'
import { Sort, EntityType } from 'util/constants'
import EntitiesSort from './EntitiesSort'
import EntitiesTypeSelector from './EntitiesTypeSelector'
import IssuesListSelector from './IssuesListSelector'
import ImpactsListSelector from './ImpactsListSelector'
import SolutionsListSelector from './SolutionsListSelector'
import { setDocumentTitle, setDefaultSiteOg } from 'util/ogMetadataUtils'
import { capitalizeFirstLetter } from 'util/textUtils'
import FeedbackListSelector from './FeedbackListSelector'
import LoadingSkeleton from 'ui/LoadingSkeleton'
import NotFoundPage from 'components/ResponsePages/404NotFoundPage'
import ScrollHideContainer from 'ui/ScrollHideContainer'
import AddFeedback from 'components/Feedback/AddFeedback'
import TeabateCarousel from 'components/Teabates/TeabateCarousel'
import StoryCarousel from 'components/Stories/StoryCarousel'

interface FeedProps {
  username?: string
  actorId?: number
  entityTypes?: EntityType[]
  hideTabList?: boolean
  isActorPage?: boolean
}

const DEFAULT_ALL_ENTITY_TYPES: EntityType[] = ['impact', 'teabate', 'solution', 'crosspost', 'feedback', 'issue', 'action', 'collection']

const Feed: React.FC<FeedProps> = ({ username, actorId, entityTypes, hideTabList = false, isActorPage = false }) => {
  const { entityType: paramEntityType, sort: paramSort = 'new' } = useParams<{
    entityType: EntityType | 'all' | ''
    sort?: Sort
  }>()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const sort = (searchParams.get('sort') as Sort) || paramSort
  const [isLoading, setIsLoading] = useState(false)  
  const [error, setError] = useState<string | null>(null)
  const [isAddFeedbackOpen, setIsAddFeedbackOpen] = useState(false)
  
  // Determine the selected entity type - default to "all" if no specific type in URL
  const selectedEntityType = paramEntityType || 'all'

  // Function to map plural to singular EntityType
  const getEntityTypeForApi = (type: string | null): EntityType | null => {
    switch (type) {
      case 'issues':
      case 'issue':
        return 'issue'
      case 'impacts':
      case 'impact':
        return 'impact'
      case 'teabates':
      case 'teabate':
        return 'teabate'
      case 'solutions':
      case 'solution':
        return 'solution'
      case 'comments':
      case 'comment':
        return 'comment'
      case 'user-feedback':
        return 'feedback'
      case 'resteeps':
      case 'resteep':
        return 'crosspost'
      case 'crossposts':
      case 'crosspost':
        return 'crosspost'
      case 'actions':
      case 'action':
        return 'action'
      case 'collections':
      case 'collection':
        return 'collection'
      case 'all':
        return null // null means all types
      default:
        return null
    }
  }

  const currentEntityType = getEntityTypeForApi(selectedEntityType)
  const resolvedEntityTypes = useMemo<EntityType[] | undefined>(() => {
    if (hideTabList) {
      return entityTypes
    }
    if (selectedEntityType !== 'all') {
      if (entityTypes) {
        return entityTypes
      }
      return currentEntityType ? [currentEntityType] : undefined
    }
    return DEFAULT_ALL_ENTITY_TYPES
  }, [hideTabList, entityTypes, selectedEntityType, currentEntityType])

  useEffect(() => {
    if (paramEntityType && paramEntityType !== 'all' && !getEntityTypeForApi(paramEntityType)) {
      setError('404: Not a supported entity type or sort option.')
    } else if (sort && !sort.match(/^top$|^bottom$|^old$|^new$/)) {
      setError('404: Not a supported sort option.')
    } else if (selectedEntityType === 'all') {
      setDocumentTitle('Societea - Spill the tea on social issues')
      setDefaultSiteOg({ title: 'Spill the tea on social issues', url: window.location.href })
    } else if (currentEntityType === 'issue' && sort !== 'top' && sort !== 'bottom' && sort !== 'old') {
      setDocumentTitle('Societea - Spill the tea on social issues')
      setDefaultSiteOg({ title: 'Spill the tea on social issues', url: window.location.href })
    } else if (currentEntityType === 'issue') {
      setDocumentTitle('Feed - Issues - ' + capitalizeFirstLetter(sort))
      setDefaultSiteOg({ title: document.title, url: window.location.href })
    } else if (currentEntityType === 'feedback') {
      setDocumentTitle('User Feedback - ' + capitalizeFirstLetter(sort))
      setDefaultSiteOg({ title: document.title, url: window.location.href })
    } else if (currentEntityType) {
      setDocumentTitle(`Feed - ${capitalizeFirstLetter(currentEntityType as EntityType)}s - ${capitalizeFirstLetter(sort)}`)
      setDefaultSiteOg({ title: document.title, url: window.location.href })
    }
  }, [currentEntityType, sort, paramEntityType, selectedEntityType])

  const handleSortChange = (newSort: Sort) => {
    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.set('sort', newSort)
    if (username) {
      navigate(`${location.pathname}?${newSearchParams.toString()}`)
    } else if (selectedEntityType === 'all') {
      navigate(`/?sort=${newSort}&${newSearchParams.toString()}`)
    } else {
      navigate(
        `/${selectedEntityType}/${newSort}?${newSearchParams.toString()}`
      )
    }
  }

  const handleTypeChange = (newType: string) => {
    const newSearchParams = new URLSearchParams(searchParams)
    if (newType === 'all') {
      navigate(`/?sort=${sort}&${newSearchParams.toString()}`)
    } else {
      navigate(`/${newType}/${sort}?${newSearchParams.toString()}`)
    }
  }

  const handleLoading = useCallback((isLoading: boolean) => {
    setIsLoading(isLoading)
  }, [])

  const renderEntitiesList = (type: string) => (
    <>
      {username ? (
        <ScrollHideContainer 
          className="z-50 max-w-5xl bg-eoe-bg dark:bg-eoe-dark-bg"
          hideOffset={115}
          threshold={114}
        >
          <div className="flex gap-1 items-center py-2 px-2">
            <EntitiesSort entityType={currentEntityType as EntityType} sort={sort} onSortChange={handleSortChange} />
          </div>
        </ScrollHideContainer>
      ) : (
        <>
        {!username && !hideTabList && (
          <div className="feed-story-carousel">
            <StoryCarousel />
          </div>
        )}
        <ScrollHideContainer 
          className={`z-50 max-w-5xl bg-eoe-bg dark:bg-eoe-dark-bg`}
          hideOffset={57}
          threshold={56}
        >
          <div className={`flex gap-1 py-2 px-2 flex-wrap ${type === 'user-feedback' ? 'flex-col' : 'items-center'}`}>
            <EntitiesTypeSelector selectedType={selectedEntityType} onTypeChange={handleTypeChange} />
            {type === 'all' && <EntitiesSort entityType={currentEntityType as EntityType} sort={sort} onSortChange={handleSortChange} />}
            {type === 'issues' && <IssuesListSelector sort={sort} searchParams={searchParams} />}
            {type === 'impacts' && <ImpactsListSelector sort={sort} searchParams={searchParams} />}
            {type === 'teabates' && <ImpactsListSelector sort={sort} searchParams={searchParams} />}
            {type === 'solutions' && <SolutionsListSelector sort={sort} searchParams={searchParams} />}
            {(type === 'comments' || type === 'crossposts' || type === 'resteeps') && <EntitiesSort entityType={currentEntityType as EntityType} sort={sort} onSortChange={handleSortChange} />}
            {type === 'actions' && <EntitiesSort entityType={currentEntityType as EntityType} sort={sort} onSortChange={handleSortChange} />}
            {type === 'collections' && <EntitiesSort entityType={currentEntityType as EntityType} sort={sort} onSortChange={handleSortChange} />}
            {type === 'user-feedback' && (
              <>
                <FeedbackListSelector 
                  sort={sort} 
                  searchParams={searchParams} 
                  onAddFeedbackClick={() => setIsAddFeedbackOpen(true)}
              />
              </>
            )}
          </div>
        </ScrollHideContainer>
              <AddFeedback
                open={isAddFeedbackOpen}
                  onClose={() => setIsAddFeedbackOpen(false)}
                />
        </>
      )}
      <EntitiesList
        entityTypes={resolvedEntityTypes}
        sort={sort as Sort}
        searchParams={searchParams}
        username={username}
        actor={actorId}
        isLoading={handleLoading}
        isActorPage={isActorPage}
      />
      {isLoading && (
        <LoadingSkeleton variant="entity-card" count={6} />
      )}
    </>
  )

  if (error) {
    return <NotFoundPage />
  }

  if (hideTabList) {
    return renderEntitiesList(selectedEntityType)
  }

  return (
    <div className="py-0 relative">
      <div className="hidden"><TeabateCarousel /></div>
      <div className="relative gap-2">
        {renderEntitiesList(selectedEntityType)}
      </div>
    </div>
  )
}

export default Feed
